/**
 * API client for shop endpoints.
 */
import { apiUrl } from './config';
import { getAuthToken, getPortalType } from '@/utils/auth';

/**
 * Shop API client
 */
export const shopAPI = {
  /**
   * Get shop items
   * @param {Object} params - Query parameters (item_type, is_active, etc.)
   */
  getItems: async (params = {}) => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }
    
    const queryParams = new URLSearchParams(params).toString();
    const url = apiUrl('/shop/items/') + (queryParams ? `?${queryParams}` : '');
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      const errorMessage = errorData.error || errorData.message || errorData.detail || `Failed to fetch shop items (${response.status})`;
      console.error('Shop items API error:', response.status, errorData);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  },

  /**
   * Get single shop item by ID
   * @param {number} itemId - Shop item ID
   */
  getItem: async (itemId) => {
    const token = getAuthToken();
    const response = await fetch(apiUrl(`/shop/items/${itemId}/`), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch shop item' }));
      throw new Error(error.error || error.message || 'Failed to fetch shop item');
    }

    return response.json();
  },

  /**
   * Purchase a shop item
   * @param {number} shopItemId - Shop item ID to purchase
   */
  purchaseItem: async (shopItemId) => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const response = await fetch(apiUrl('/shop/purchase/'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ shop_item_id: shopItemId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to purchase item' }));
      throw new Error(error.error || error.message || 'Failed to purchase item');
    }

    const data = await response.json();
    // Ensure is_consumable is set correctly
    if (data.shop_item) {
      const itemType = data.shop_item.item_type;
      if (!data.hasOwnProperty('is_consumable')) {
        data.is_consumable = itemType === 'BOOSTER' || itemType === 'PROTECTOR';
      }
    }
    return data;
  },

  /**
   * Get student's inventory
   * @param {Object} params - Query parameters (is_equipped, shop_item__item_type, etc.)
   */
  getInventory: async (params = {}) => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const queryParams = new URLSearchParams(params).toString();
    const url = apiUrl('/users/inventory/') + (queryParams ? `?${queryParams}` : '');
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch inventory' }));
      throw new Error(error.error || error.message || 'Failed to fetch inventory');
    }

    return response.json();
  },

  /**
   * Equip or unequip an inventory item
   * @param {number} inventoryItemId - Inventory item ID
   */
  equipItem: async (inventoryItemId) => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    const response = await fetch(apiUrl(`/users/inventory/${inventoryItemId}/equip/`), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to equip item' }));
      throw new Error(error.error || error.message || 'Failed to equip item');
    }

    return response.json();
  },
};
